'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  icon: 'BuildingStorefrontIcon' | 'HandshakeIcon' | 'GlobeAltIcon' | 'ArrowTrendingUpIcon';
  value: number;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  {
    id: 'stat_1',
    icon: 'BuildingStorefrontIcon',
    value: 150,
    suffix: '+',
    label: 'Exhibitions Managed',
  },
  {
    id: 'stat_2',
    icon: 'HandshakeIcon',
    value: 2500,
    suffix: '+',
    label: 'Deals Facilitated',
  },
  {
    id: 'stat_3',
    icon: 'GlobeAltIcon',
    value: 40,
    suffix: '+',
    label: 'Countries Covered',
  },
  {
    id: 'stat_4',
    icon: 'ArrowTrendingUpIcon',
    value: 95,
    suffix: '%',
    label: 'Client Retention Rate',
  },
];

export default function ResultsCounter() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>(
    stats.reduce((acc, stat) => ({ ...acc, [stat.id]: 0 }), {})
  );
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    stats.forEach((stat) => {
      let currentStep = 0;
      const increment = stat.value / steps;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.round(increment * currentStep), stat.value);

        setCounts((prev) => ({
          ...prev,
          [stat.id]: newValue,
        }));

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="section-padding bg-gradient-to-b from-primary to-accent">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeIn">
          <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-white">
              Results by Numbers
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white">
            Proven Track Record
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className="glass rounded-3xl p-8 text-center space-y-4 hover-lift animate-scaleIn"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto">
                <Icon name={stat.icon} size={32} className="text-white" />
              </div>

              <div className="text-5xl md:text-6xl font-bold text-white">
                {counts[stat.id]}
                <span className="text-accent">{stat.suffix}</span>
              </div>

              <div className="text-sm font-medium uppercase tracking-wider text-white/80">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}